import { useState } from 'react';
import { EmptyState, StatusPill, TextButton, type StatusLevel } from '@/lib';
import type { ApiClients } from '@/api/client';
import { useAuditLog, useAuditLogStats, type AuditEntry, type AuditStats } from './queries';

export type SAuditLogProps = { clients: ApiClients };

type TargetFilter =
  | 'all'
  | 'user'
  | 'group'
  | 'invitation'
  | 'org'
  | 'alert'
  | 'sensor'
  | 'asset';

type WindowChoice = 24 | 168 | 720;

const ACTION_TONE: Record<string, StatusLevel> = {
  created: 'info',
  updated: 'watch',
  deleted: 'critical',
  deactivated: 'critical',
  activated: 'healthy',
  role_changed: 'watch',
  accepted: 'healthy',
  reset: 'warn',
  member_added: 'info',
  member_removed: 'watch',
  pair: 'info',
  assigned: 'info',
  baseline_captured: 'info',
};

function toneFor(action: string): StatusLevel {
  const tail = action.includes('.') ? action.split('.', 2)[1] : action;
  return ACTION_TONE[tail] ?? 'watch';
}

/**
 * Audit log read view at /admin/audit. Reads R5's GET /v1/audit-log
 * which is admin-gated (cap org.audit_log). Filter chips along the
 * top scope to one target_type; everything else is just a sortable
 * table.
 */
export function SAuditLog({ clients }: SAuditLogProps) {
  const [filter, setFilter] = useState<TargetFilter>('all');
  const [windowHours, setWindowHours] = useState<WindowChoice>(24);
  const audit = useAuditLog(clients, {
    targetType: filter === 'all' ? undefined : filter,
    limit: 200,
  });
  const stats = useAuditLogStats(clients, { windowHours });

  if (audit.isError) {
    return (
      <EmptyState
        variant="error"
        title="Couldn't load audit log"
        body={String(audit.error)}
        cta={{ label: 'Retry', onClick: () => audit.refetch() }}
      />
    );
  }

  const items = audit.data ?? [];

  return (
    <div className="flex flex-col gap-sp-6">
      <header className="flex items-baseline justify-between">
        <div>
          <p className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
            Access control
          </p>
          <h1 className="mt-sp-1 text-fs-h1 font-medium text-gray-900">Audit log</h1>
          <p className="mt-sp-1 text-fs-meta text-gray-700">
            {items.length} most-recent mutation{items.length === 1 ? '' : 's'}
            {filter !== 'all' ? ` · target ${filter}` : ''}
          </p>
        </div>
        <TextButton label="Refresh" onClick={() => audit.refetch()} />
      </header>

      {stats.data ? (
        <StatsHero
          stats={stats.data}
          windowHours={windowHours}
          onWindowChange={setWindowHours}
        />
      ) : null}

      <FilterChips current={filter} onChange={setFilter} />

      {audit.isLoading ? (
        <p className="text-fs-body text-gray-700">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-fs-body text-gray-700">No matching audit entries.</p>
      ) : (
        <div className="overflow-x-auto rounded-r-card border border-gray-300 bg-gray-50">
          <table className="min-w-full text-fs-meta">
            <thead>
              <tr className="text-gray-700">
                <Th>When</Th>
                <Th>Actor</Th>
                <Th>Action</Th>
                <Th>Target</Th>
              </tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <Row key={e.id} entry={e} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const WINDOW_LABEL: Record<WindowChoice, string> = {
  24: '24h',
  168: '7d',
  720: '30d',
};

function StatsHero({
  stats,
  windowHours,
  onWindowChange,
}: {
  stats: AuditStats;
  windowHours: WindowChoice;
  onWindowChange: (w: WindowChoice) => void;
}) {
  const topAction = topKey(stats.by_action);
  const topTarget = topKey(stats.by_target_type);
  return (
    <section className="flex flex-col gap-sp-2" aria-label={`Last ${WINDOW_LABEL[windowHours]}`}>
      <WindowToggle current={windowHours} onChange={onWindowChange} />
      <dl className="grid grid-cols-2 gap-sp-3 sm:grid-cols-4">
        <Pill
          label={`Events / ${WINDOW_LABEL[windowHours]}`}
          value={String(stats.total_count)}
        />
        <Pill label="Unique actors" value={String(stats.unique_actors)} />
        <Pill label="Top action" value={topAction ?? '—'} mono />
        <Pill label="Top target" value={topTarget ?? '—'} mono />
      </dl>
    </section>
  );
}

function WindowToggle({
  current,
  onChange,
}: {
  current: WindowChoice;
  onChange: (w: WindowChoice) => void;
}) {
  const opts: WindowChoice[] = [24, 168, 720];
  return (
    <div
      role="radiogroup"
      aria-label="Stats window"
      className="inline-flex w-fit items-center gap-sp-1 rounded-r-default border border-gray-300 bg-gray-50 p-sp-1"
    >
      {opts.map((o) => (
        <button
          key={o}
          role="radio"
          aria-checked={current === o}
          onClick={() => onChange(o)}
          className={
            'rounded-r-default px-sp-2 py-sp-1 text-fs-meta ' +
            (current === o
              ? 'bg-gray-900 text-gray-50'
              : 'text-gray-900 hover:bg-gray-100')
          }
        >
          {WINDOW_LABEL[o]}
        </button>
      ))}
    </div>
  );
}

function Pill({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="rounded-r-card border border-gray-300 bg-gray-50 px-sp-4 py-sp-3">
      <dt className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
        {label}
      </dt>
      <dd className={'mt-sp-1 text-fs-h2 text-gray-900' + (mono ? ' font-mono text-fs-h3' : '')}>
        {value}
      </dd>
    </div>
  );
}

function topKey(counts: Record<string, number>): string | null {
  let best: string | null = null;
  let bestN = 0;
  for (const [k, n] of Object.entries(counts ?? {})) {
    if (n > bestN) {
      best = k;
      bestN = n;
    }
  }
  return best;
}

function FilterChips({
  current,
  onChange,
}: {
  current: TargetFilter;
  onChange: (f: TargetFilter) => void;
}) {
  const opts: TargetFilter[] = [
    'all',
    'user',
    'group',
    'invitation',
    'org',
    'alert',
    'sensor',
    'asset',
  ];
  return (
    <div className="flex items-center gap-sp-2" role="tablist" aria-label="Filter by target">
      {opts.map((o) => (
        <button
          key={o}
          role="tab"
          aria-selected={current === o}
          onClick={() => onChange(o)}
          className={
            'rounded-r-default px-sp-3 py-sp-1 text-fs-meta ' +
            (current === o
              ? 'bg-gray-900 text-gray-50'
              : 'border border-gray-300 bg-gray-50 text-gray-900 hover:border-gray-500')
          }
        >
          {o}
        </button>
      ))}
    </div>
  );
}

function Row({ entry }: { entry: AuditEntry }) {
  const when = new Date(entry.at).toLocaleString();
  const actor = entry.actor_email || entry.actor || '—';
  return (
    <tr className="border-t border-gray-300 align-top">
      <td className="px-sp-3 py-sp-2 text-gray-900">
        <time dateTime={entry.at} className="font-mono">
          {when}
        </time>
      </td>
      <td className="px-sp-3 py-sp-2 text-gray-900">{actor}</td>
      <td className="px-sp-3 py-sp-2">
        <span className="font-mono text-gray-900">{entry.action}</span>
        <span className="ml-sp-2 inline-block align-middle">
          <StatusPill level={toneFor(entry.action)} label={tail(entry.action)} />
        </span>
      </td>
      <td className="px-sp-3 py-sp-2 text-gray-700">
        <span className="font-mono">{entry.target_type}</span>
        {entry.target_id ? (
          <>
            {' '}
            ·{' '}
            <code className="font-mono">{entry.target_id}</code>
          </>
        ) : null}
      </td>
    </tr>
  );
}

function tail(action: string): string {
  const idx = action.indexOf('.');
  return idx >= 0 ? action.slice(idx + 1).toUpperCase() : action.toUpperCase();
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="px-sp-3 py-sp-2 text-left font-medium uppercase tracking-[0.14em]">
      {children}
    </th>
  );
}
